import type { Invoice, InvoiceStatus } from '@/api/types';

export type InvoiceSort = 'date-desc' | 'date-asc' | 'due-asc' | 'due-desc';

export const sortLabel: Record<InvoiceSort, string> = {
  'date-desc': 'Сначала новые',
  'date-asc': 'Сначала старые',
  'due-asc': 'По сроку: ближайшие',
  'due-desc': 'По сроку: дальние',
};

export type InvoiceFilter = { clientId: string; status: InvoiceStatus | ''; q: string; sort: InvoiceSort };

export const noFilter: InvoiceFilter = { clientId: '', status: '', q: '', sort: 'date-desc' };

/** The studio's invoice list as the filters ask: a client, a status, a number (or a part of it), an order. */
export function filterInvoices(invoices: Invoice[], f: InvoiceFilter): Invoice[] {
  const q = f.q.trim().replace(/^№\s*/, '').toLowerCase();
  const key = f.sort.startsWith('due') ? (i: Invoice) => i.dueAt : (i: Invoice) => i.issuedAt;
  const dir = f.sort.endsWith('asc') ? 1 : -1;
  return invoices
    .filter((i) => (!f.clientId || i.clientId === f.clientId) && (!f.status || i.status === f.status) && (!q || i.number.toLowerCase().includes(q)))
    .sort((a, b) => dir * key(a).localeCompare(key(b)) || dir * a.number.localeCompare(b.number));
}

/** The clients that have invoices, for the filter's options. */
export function clientsOf(invoices: Invoice[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const i of invoices) if (i.clientId) seen.set(i.clientId, i.clientName ?? i.clientId);
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/** An unpaid invoice can be cancelled; a paid or part-paid one is a refund, not a cancel (the BFF refuses it too). */
export const cancellable = (i: Invoice) => i.status !== 'paid' && i.status !== 'draft' && i.paid.amountMinor === 0;

/** A reminder within the last 24 hours is the BFF's throttle; the button waits it out. */
export const remindedRecently = (i: Invoice, now = Date.now()) => !!i.remindedAt && now - new Date(i.remindedAt).getTime() < 24 * 3_600_000;

/** Sent, overdue or being checked: money may have come in a way the bank statement does not show. */
export const markable = (i: Invoice) => i.status === 'sent' || i.status === 'overdue' || i.status === 'processing';

/** "1 234,50" or "1234.5" in major units to minor units; NaN when it is not a sum. */
export function parseMajor(raw: string): number {
  const t = raw.replace(/\s| /g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return NaN;
  return Math.round(parseFloat(t) * 100);
}
